import { useState } from "react";
import { useIntl } from "react-intl";

import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import { saveLearningCenterConsent } from "~/hooks/learning-center/guest-learning-data";
import useLearningCenter from "~/hooks/use-learning-center";
import { useRootLoaderData } from "~/root";
import { useSetTpaConsent } from "~/segment/use-tpa-consent";
import { sanitizeHtml } from "~/utils/sanitize-html";

import { tpaLearningCenterConsentMessages as msg } from "./messages";

export interface TpaLearningCenterConsentModalProps {
  /** Builder-authored HTML body for the modal (trusted `tpa-learning-center-consent` model). */
  modalContent?: string;
  className?: string;
  attributes?: Record<string, unknown>;
}

/**
 * TPA Learning Center consent modal (SFCC-11508).
 *
 * Port of hec-composable
 * `hec/components/tpa-learning-center-consent/tpaLearningCenterConsent.tsx`.
 * Open by default, NO close button (an explicit Yes/No choice is required).
 * Renders the Builder `modalContent` HTML (sanitized; the model is trusted).
 *
 * THREE writes on dismiss (mirroring composable `closeDialog`):
 *   1. localStorage ALWAYS (guest + registered) via `saveLearningCenterConsent`,
 *      so the answer suppresses the modal on the next render for anyone.
 *   2. `useSetTpaConsent()` (the `/resources/tpa-consent` write +
 *      `setExplicitConsent`) ONLY on `accept && registered`.
 *   3. `updateConsentData(consent)` for ANY value when registered — which
 *      stamps the in-blob `tpaLearningCenterConsentData` AND, via the
 *      learning-center resource action, the top-level
 *      `c_tpaLearningCenterConsentData`. DECLINE therefore persists `false`
 *      to both (consent-integrity: a no-op decline would be a bug).
 *
 * The guard (`!hasRespondedtoConsentModal()`) lives at the mount site, so this
 * component renders at most once per answered browser.
 */
export function TpaLearningCenterConsentModal({
  modalContent,
  className,
  attributes,
}: TpaLearningCenterConsentModalProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(true);
  const { isRegistered } = useRootLoaderData();
  const { updateConsentData } = useLearningCenter();
  const setTpaConsent = useSetTpaConsent();

  const { className: attrClassName, ...restAttributes } = (attributes ??
    {}) as { className?: string; [key: string]: unknown };

  const closeDialog = async (consent: boolean) => {
    // (1) localStorage ALWAYS — guest + registered.
    saveLearningCenterConsent(consent);

    // (2) Explicit marketing-consent profile write — opt-in by a registered
    // shopper only (un-suppresses Segment via the root revalidate).
    if (consent && isRegistered) {
      setTpaConsent();
    }

    // (3) Persist to the profile for ANY answer when registered. Writes the
    // in-blob flag + top-level c_tpaLearningCenterConsentData (incl. `false`).
    if (isRegistered) {
      try {
        await updateConsentData(consent);
      } catch {
        // A failed consent persist must never block the dismiss.
      }
    }

    setOpen(false);
  };

  return (
    <Dialog open={open} data-testid="tpa-learning-center-consent-dialog">
      <DialogContent
        className="max-[700px]:w-[calc(100vw-1.5rem)] max-[700px]:px-4 max-[700px]:py-6 max-h-[calc(100vh-2rem)] max-w-[48rem] gap-0 overflow-y-auto px-[3.25rem] py-10"
        hideCloseBtn
        data-testid="tpa-learning-center-consent-content"
        {...restAttributes}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{intl.formatMessage(msg.title)}</DialogTitle>
          <DialogDescription>
            {intl.formatMessage(msg.description)}
          </DialogDescription>
        </VisuallyHidden.Root>

        <div className={className ?? attrClassName}>
          {modalContent ? (
            <div
              data-testid="tpa-learning-center-consent-body"
              // Trusted Builder `tpa-learning-center-consent` model; sanitized
              // (allowStyle parity with the TPA welcome banner — authored copy
              // may carry an inline <style> block).
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(modalContent, { allowStyle: true }),
              }}
            />
          ) : null}
        </div>

        <div className="w-full text-right">
          <button
            type="button"
            className="w-1/2 rounded-[2.1875rem] border-[#ff295b] bg-[#DADADA] px-8 py-3.5 text-sm font-bold leading-normal text-[#727272] sm:mr-4 sm:w-36"
            onClick={() => closeDialog(false)}
            data-testid="tpa-learning-center-consent-decline"
          >
            {intl.formatMessage(msg.decline)}
          </button>
          <button
            type="button"
            className="w-1/2 rounded-[2.1875rem] border-[#ff295b] bg-[#49388E] px-9 py-3.5 font-grotesque text-sm font-bold leading-normal text-white sm:w-64"
            onClick={() => closeDialog(true)}
            data-testid="tpa-learning-center-consent-accept"
          >
            {intl.formatMessage(msg.accept)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
